import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Money,
  Moment,
  MoneyInput,
  Notice,
  Select,
  SkeletonRows,
  StatusBadge,
  Table,
  TextInput,
} from '../../shell/kit';
import { errorText } from '../../shell/errors';
import { formatMoney } from '../../shell/format';
import { useToast } from '../../shell/toast';
import {
  attachReferral,
  referralPolicy,
  referrals as loadReferrals,
  referralSummary,
  saveReferralPolicy,
  type ReferralPolicy,
  type ReferralSource,
  type ReferralStatus,
  type ReferralSummary,
  type ReferralView,
} from './api';
import type { LoyaltyKey, LoyaltyTranslate } from './i18n';

/**
 * Реферальная механика (ТЗ §7 М4.7).
 *
 * Вкладка отвечает на два вопроса маркетолога: на каких условиях работает
 * программа и кто кого привёл — через какой канал и получил ли пригласивший
 * бонус. Карточка в разделе «Клиент» показывает код приглашения клиента и
 * позволяет записать пригласившего, если код назвали не на кассе.
 */

const SOURCES: readonly ReferralSource[] = ['pos', 'tablet', 'admin', 'link', 'import'];

const STATUS_TONE: Readonly<Record<ReferralStatus, 'neutral' | 'ok' | 'danger'>> = {
  pending: 'neutral',
  rewarded: 'ok',
  rejected: 'danger',
};

export interface ReferralsTabProps {
  readonly t: LoyaltyTranslate;
  readonly canManage: boolean;
  readonly onError: (message: string) => void;
}

export function ReferralsTab({ t, canManage, onError }: ReferralsTabProps) {
  const toast = useToast();
  const [policy, setPolicy] = useState<ReferralPolicy | null>(null);
  const [rows, setRows] = useState<readonly ReferralView[] | null>(null);
  const [source, setSource] = useState<ReferralSource | ''>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    referralPolicy()
      .then((value) => {
        if (alive) setPolicy(value);
      })
      .catch((err: Error) => {
        if (alive) onError(errorText(err).message);
      });
    return () => {
      alive = false;
    };
  }, [onError]);

  useEffect(() => {
    let alive = true;
    setRows(null);
    loadReferrals(source || undefined)
      .then((value) => {
        if (alive) setRows(value.referrals);
      })
      .catch((err: Error) => {
        if (alive) {
          setRows([]);
          onError(errorText(err).message);
        }
      });
    return () => {
      alive = false;
    };
  }, [source, onError]);

  function patch(next: Partial<ReferralPolicy>): void {
    setPolicy((current) => (current ? { ...current, ...next } : current));
  }

  async function save(): Promise<void> {
    if (!policy) return;
    setBusy(true);
    try {
      setPolicy(await saveReferralPolicy(policy));
      toast.ok(t('ref.policy.saved'));
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card title={t('ref.policy.title')} hint={t('ref.policy.hint')}>
        {!policy ? (
          <SkeletonRows rows={3} />
        ) : (
          <>
            <label className="mb-4 flex items-center gap-2 text-sm text-ink">
              <Checkbox
                checked={policy.enabled}
                disabled={!canManage}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              {t('ref.policy.enabled')}
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('ref.policy.referrer')}>
                <MoneyInput
                  disabled={!canManage}
                  valueMinor={policy.referrerRewardMinor}
                  onChangeMinor={(minor) => patch({ referrerRewardMinor: minor ?? 0 })}
                />
              </Field>
              <Field label={t('ref.policy.invitee')}>
                <MoneyInput
                  disabled={!canManage}
                  valueMinor={policy.inviteeRewardMinor}
                  onChangeMinor={(minor) => patch({ inviteeRewardMinor: minor ?? 0 })}
                />
              </Field>
              <Field label={t('ref.policy.minOrder')}>
                <MoneyInput
                  disabled={!canManage}
                  valueMinor={policy.minOrderMinor}
                  onChangeMinor={(minor) => patch({ minOrderMinor: minor ?? 0 })}
                />
              </Field>
              <Field label={t('ref.policy.expires')} hint={t('ref.policy.expiresHint')}>
                <TextInput
                  inputMode="numeric"
                  disabled={!canManage}
                  value={policy.rewardExpiresInDays === null ? '' : String(policy.rewardExpiresInDays)}
                  onChange={(e) => {
                    const days = Number(e.target.value.replace(/\D/g, ''));
                    patch({ rewardExpiresInDays: days > 0 ? days : null });
                  }}
                />
              </Field>
            </div>
            <div className="mt-4">
              {canManage ? (
                <Button tone="primary" disabled={busy} onClick={() => void save()}>
                  {t('ref.policy.save')}
                </Button>
              ) : (
                <Notice tone="info">{t('ref.policy.readonly')}</Notice>
              )}
            </div>
          </>
        )}
      </Card>

      <Card
        title={t('ref.list.title')}
        hint={t('ref.list.hint')}
        actions={
          <div className="w-48">
            <Select value={source} onChange={(e) => setSource(e.target.value as ReferralSource | '')}>
              <option value="">{t('ref.list.allSources')}</option>
              {SOURCES.map((value) => (
                <option key={value} value={value}>
                  {t(`ref.source.${value}` as LoyaltyKey)}
                </option>
              ))}
            </Select>
          </div>
        }
      >
        {rows === null ? (
          <SkeletonRows rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState title={t('ref.list.empty')} reason={t('ref.list.emptyHint')} />
        ) : (
          <div className="overflow-x-auto">
            <Table className="text-left">
              <thead className="bg-sunken text-xs uppercase text-ink-muted">
                <tr>
                  <th className="px-3 py-2">{t('ref.list.date')}</th>
                  <th className="px-3 py-2">{t('ref.list.referrer')}</th>
                  <th className="px-3 py-2">{t('ref.list.invitee')}</th>
                  <th className="px-3 py-2">{t('ref.list.source')}</th>
                  <th className="px-3 py-2">{t('ref.list.status')}</th>
                  <th className="px-3 py-2">{t('ref.list.reward')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-line">
                    <td className="px-3 py-2 text-xs">
                      <Moment iso={row.createdAt} />
                    </td>
                    <td className="px-3 py-2">{row.referrerName ?? row.code}</td>
                    <td className="px-3 py-2">{row.inviteeName ?? '—'}</td>
                    <td className="px-3 py-2">{t(`ref.source.${row.source}` as LoyaltyKey)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={STATUS_TONE[row.status]}>
                        {t(`ref.status.${row.status}` as LoyaltyKey)}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-2">
                      <Money minor={row.referrerRewardMinor} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

export interface ReferralCardProps {
  readonly t: LoyaltyTranslate;
  readonly customerId: string;
  readonly onError: (message: string) => void;
}

export function ReferralCard({ t, customerId, onError }: ReferralCardProps) {
  const toast = useToast();
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setSummary(await referralSummary(customerId));
    } catch (err) {
      onError(errorText(err).message);
    }
  }, [customerId, onError]);

  useEffect(() => {
    setSummary(null);
    setCode('');
    void load();
  }, [load]);

  async function attach(): Promise<void> {
    setBusy(true);
    try {
      await attachReferral(customerId, code.trim());
      toast.ok(t('ref.card.attached'));
      setCode('');
      await load();
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('ref.card.title')}>
      {!summary ? (
        <SkeletonRows rows={2} />
      ) : (
        <div className="space-y-3 text-sm">
          <p>
            <span className="text-ink-muted">{t('ref.card.code')}: </span>
            <span className="font-mono text-base font-semibold tracking-wider">{summary.code}</span>
          </p>
          <p>
            <span className="text-ink-muted">{t('ref.card.invitedBy')}: </span>
            {summary.invitedBy ? (
              <>
                {summary.invitedBy.referrerName ?? summary.invitedBy.code} ·{' '}
                {t(`ref.source.${summary.invitedBy.source}` as LoyaltyKey)} ·{' '}
                {t(`ref.status.${summary.invitedBy.status}` as LoyaltyKey)}
              </>
            ) : (
              <span className="text-ink-muted">{t('ref.card.none')}</span>
            )}
          </p>
          <p className="text-ink-muted">
            {t('ref.card.stats', {
              count: summary.invitedCount,
              rewarded: summary.rewardedCount,
              amount: formatMoney(summary.earnedMinor),
            })}
          </p>
          {summary.invitedBy ? null : (
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t('ref.card.attachCode')}>
                <TextInput
                  value={code}
                  placeholder="ABCD2345"
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                />
              </Field>
              <Button tone="primary" disabled={busy || code.trim().length < 4} onClick={() => void attach()}>
                {t('ref.card.attach')}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
